"use client";

// This is now ExhibitClient.tsx
import AnimatedLogoClouds from "@/components/AnimatedLogoClouds";
import { Container } from "@/components/Container";
import { AdaptiveText, type AdaptiveTextProps } from "@/components/Content";
import { Label } from "@/components/ui/label";
import type { ImageDetails } from "@/types";
import { motion } from "framer-motion";
import Image from "next/image";

type Props = {
	testimonial: AdaptiveTextProps["portableText"];
	author: {
		name: string;
		role: string;
		company: string;
	};
	companyLogo?: ImageDetails;
	logoCloudsTitle: string;
	logos: ImageDetails[];
	id?: string;
	tweetCard: React.ReactNode;
};

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.15,
			delayChildren: 0.2,
		},
	},
};

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.6,
			ease: "easeOut" as const,
		},
	},
};

const logoCloudVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			duration: 0.8,
			delay: 0.5,
			ease: "easeOut" as const,
		},
	},
};

const ExhibitClient = ({
	id,
	testimonial,
	author,
	companyLogo,
	logoCloudsTitle,
	logos,
	tweetCard,
}: Props) => {
	const duplicatedLogos = [...logos, ...logos, ...logos, ...logos];

	return (
		<section id={id} className="relative overflow-hidden py-16 md:py-24">
			<Container className="relative flex flex-col items-center justify-around gap-16 xl:flex-row">
				<motion.div
					className="tweet-card-wrapper"
					initial={{ opacity: 0, x: 30 }}
					whileInView={{ opacity: 1, x: 0 }}
					viewport={{ once: true, margin: "-50px" }}
					transition={{
						duration: 0.7,
						delay: 0.3,
						ease: "easeOut",
					}}
				>
					{tweetCard}
				</motion.div>

				<motion.div
					className="mx-auto w-full max-w-xl"
					variants={containerVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-50px" }}
				>
					{companyLogo && (
						<motion.div className="h-16 w-16" variants={itemVariants}>
							<Image
								src={companyLogo.src}
								alt={companyLogo.alt}
								width={companyLogo.width || 256}
								height={companyLogo.height || 256}
								className="h-16 w-16"
							/>
						</motion.div>
					)}
					<motion.div variants={itemVariants}>
						<AdaptiveText portableText={testimonial} />
					</motion.div>
					<motion.div className="mt-6" variants={itemVariants}>
						<Label className="text-sm sm:text-lg">~</Label>
						<Label className="font-bold text-sm text-zinc-700 sm:text-lg">
							{author.name},{" "}
						</Label>
						<Label className="text-sm sm:text-lg">
							{author.role} at {author.company}
						</Label>
					</motion.div>

					<motion.div
						className="mt-16 w-full md:mt-24"
						variants={logoCloudVariants}
					>
						<h2 className="text-center font-semibold text-sm text-zinc-700 md:text-lg md:leading-8">
							{logoCloudsTitle}
						</h2>
						<div className="relative isolate mx-auto mt-6 overflow-hidden">
							<div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-linear-to-r from-white md:w-24" />
							<div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-linear-to-l from-white md:w-24" />
							<AnimatedLogoClouds
								logos={duplicatedLogos}
								className="gap-x-1 md:gap-x-2"
								logoClassname="max-h-12 md:max-h-14"
							/>
						</div>
					</motion.div>
				</motion.div>
			</Container>
		</section>
	);
};

export default ExhibitClient;
